import { useAuth } from 'hooks';
import React, { createRef, useEffect, useMemo, useState } from 'react';

import styles from '../DeviceMap.module.scss';
import {
    BottomNavigation,
    BottomNavigationAction,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
} from '@mui/material';
import { FilterList, PropaneTank } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import { updateUserSession } from 'lib/firebase';
import { DeviceMapFilters } from '../DeviceMap';
import { Device } from 'types';
import { DeviceMapRow } from './DeviceMapRow';
import { activateSnackbar } from 'store/features/ui';
import { useDispatch } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { isDeviceVisible } from '../helpers';

interface DeviceMapSidePanelProps {
    devices: Device[];
    mapFilters: DeviceMapFilters;
    setMapFilters: React.Dispatch<React.SetStateAction<DeviceMapFilters>>;
    mapRef: google.maps.Map;
    selectedMarkerId: string | null;
    setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | null>>;
}

export function DeviceMapSidePanel({
    devices,
    mapFilters,
    setMapFilters,
    mapRef,
    selectedMarkerId,
    setSelectedMarkerId,
}: DeviceMapSidePanelProps) {
    const navigate = useNavigate();
    const [currentMapTab, setCurrentMapTab] = useState(0);
    const listRef = createRef<any>();
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

    const shownDevices = useMemo(() => {
        return devices.filter((d) => {
            const isVisible = isDeviceVisible(d, mapFilters);

            return isVisible;
        });
    }, [devices, mapFilters]);

    useEffect(() => {
        if (selectedMarkerId) {
            setSelectedDevice(null);
            const index = shownDevices.findIndex(
                (d) => d.deviceId === selectedMarkerId
            );
            if (index && listRef.current) {
                listRef.current.scrollToItem(index, 'smart');
            }
        }
    }, [shownDevices, listRef, selectedMarkerId, setSelectedMarkerId]);

    return (
        <Box className={styles.sidePanel}>
            <BottomNavigation
                showLabels
                value={currentMapTab}
                onChange={(event, newValue) => {
                    setCurrentMapTab(newValue);
                }}
                className={styles.sideNavOptions}
            >
                <BottomNavigationAction
                    label="Monitors"
                    icon={<PropaneTank />}
                />
                <BottomNavigationAction label="Filters" icon={<FilterList />} />
            </BottomNavigation>
            <Box className={styles.tankListContainer}>
                {currentMapTab === 0 ? (
                    <>
                        {shownDevices.length ? (
                            <AutoSizer>
                                {({ height, width }: any) => {
                                    return (
                                        <FixedSizeList
                                            height={height}
                                            width={width}
                                            overscanCount={4}
                                            itemCount={shownDevices.length}
                                            itemSize={100}
                                            itemData={shownDevices}
                                            ref={listRef}
                                        >
                                            {({ data, index, style }) => {
                                                return (
                                                    <DeviceMapRow
                                                        selectedMarkerId={
                                                            selectedMarkerId
                                                        }
                                                        setSelectedMarkerId={
                                                            setSelectedMarkerId
                                                        }
                                                        device={data[index]}
                                                        mapRef={mapRef}
                                                        navigate={navigate}
                                                        style={style}
                                                        onDeviceSelected={
                                                            setSelectedDevice
                                                        }
                                                        selectedDevice={
                                                            selectedDevice
                                                        }
                                                    />
                                                );
                                            }}
                                        </FixedSizeList>
                                    );
                                }}
                            </AutoSizer>
                        ) : (
                            <Box className={styles.noDevicesPlaceholder}>
                                No Devices found...
                            </Box>
                        )}
                    </>
                ) : (
                    <MapFilters
                        mapFilters={mapFilters}
                        setMapFilters={setMapFilters}
                    />
                )}
            </Box>
        </Box>
    );
}

interface MapFiltersProps {
    mapFilters: DeviceMapFilters;
    setMapFilters: React.Dispatch<React.SetStateAction<DeviceMapFilters>>;
}

function MapFilters({ mapFilters, setMapFilters }: MapFiltersProps) {
    const { user } = useAuth();

    const dispatch = useDispatch();

    const [localFilterMap, setLocalFilterMap] =
        useState<DeviceMapFilters>(mapFilters);

    const filterDisplayMap: Record<keyof DeviceMapFilters, string> = {
        doCluster: 'Marker Clustering',
        v0: '0-20%',
        v20: '20-40%',
        v40: '40-60%',
        v60: '60-80%',
        v80: '80-100%',
    };

    const handleFilterSave = async () => {
        setMapFilters(localFilterMap);

        if (user?.uid) {
            await updateUserSession(user.uid, 'map', localFilterMap);
            dispatch(
                activateSnackbar({
                    message: 'Filters successfully updated',
                    severity: 'success',
                })
            );
        }
    };

    const filterOptions = Object.keys(localFilterMap).map((filter) => {
        const filterKey = filter as keyof typeof localFilterMap;

        const label = filterDisplayMap[filterKey];
        const handleFilterKeyChange = () => {
            const newFilterMap = {
                ...localFilterMap,
                [filter]: !localFilterMap[filterKey],
            };

            setLocalFilterMap(newFilterMap);
        };

        return (
            <FormControlLabel
                key={filter}
                control={
                    <Checkbox
                        checked={localFilterMap[filterKey] === true}
                        onClick={handleFilterKeyChange}
                    />
                }
                label={label}
            />
        );
    });

    return (
        <Box className={styles.filterGroupContainer}>
            <Box className={styles.formGroups}>
                <FormGroup className={styles.filterGroup}>
                    {filterOptions.slice(0, 3)}
                </FormGroup>
                <FormGroup className={styles.filterGroup}>
                    {filterOptions.slice(3, filterOptions.length)}
                </FormGroup>
            </Box>

            <Button variant="contained" onClick={handleFilterSave}>
                Save
            </Button>
        </Box>
    );
}
